// MindTab — Phase 1 scaffold: Today's overview + settings
import React, { useState } from 'react';
import { useMind } from '../../../context/MindContext';
import { DEFAULT_MIND_SETTINGS, INTENTION_OPTIONS } from '../../../config/mindConstants';
import ReadinessGauge from '../mind/ReadinessGauge';
import { FaBrain, FaBolt, FaCrosshairs, FaChartLine, FaShieldAlt, FaCog } from 'react-icons/fa';

export default function MindTab() {
  const {
    settings, dailyState, performance, isCheckedIn,
    readinessScore, energyScore, readinessLevel, energyLevel,
    cooldownActive, cooldownRemaining,
    setShowPreTradeCheck, updateMindSettings,
  } = useMind();
  const [view, setView] = useState('overview');
  const [editSettings, setEditSettings] = useState(null);

  const formatTime = (ms) => {
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}m ${s}s`;
  };

  return (
    <div className="space-y-4">
      {/* Sub-nav */}
      <div className="flex items-center gap-1">
        {[
          { key: 'overview', label: 'Overview', icon: <FaBrain /> },
          { key: 'settings', label: 'Settings', icon: <FaCog /> },
        ].map(v => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
              view === v.key ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span className="text-[10px]">{v.icon}</span>
            {v.label}
          </button>
        ))}
      </div>

      {view === 'overview' && (
        <div className="space-y-4">
          {/* Check-in status */}
          {!isCheckedIn ? (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-3 bg-blue-50 rounded-2xl flex items-center justify-center">
                <FaBrain className="text-xl text-blue-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">No check-in today</h3>
              <p className="text-xs text-gray-500 mb-4">Complete your daily readiness check to activate the Mind Layer.</p>
              <button
                onClick={() => setShowPreTradeCheck(true)}
                className="px-4 py-2 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                Check In Now
              </button>
            </div>
          ) : (
            <>
              {/* Scores */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                  <ReadinessGauge score={readinessScore} level={readinessLevel} label="Readiness" />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                  <ReadinessGauge score={energyScore} level={energyLevel} label="Energy" />
                </div>
              </div>

              {/* Today's stats */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <h4 className="text-xs font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
                  <FaChartLine className="text-blue-500 text-[10px]" /> Today's Activity
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Stat label="Intention" value={dailyState?.intention?.split('—')[0].trim() || '—'} icon={<FaCrosshairs />} />
                  <Stat label="Trades" value={`${performance?.tradesToday || 0}/${settings?.maxTradesPerDay || 10}`} icon={<FaChartLine />} />
                  <Stat
                    label="Daily P&L"
                    value={`₹${(performance?.dailyPnL || 0).toLocaleString()}`}
                    icon={<FaBolt />}
                    color={(performance?.dailyPnL || 0) >= 0 ? 'text-green-600' : 'text-red-600'}
                  />
                  <Stat label="Loss Streak" value={performance?.consecutiveLosses || 0} icon={<FaShieldAlt />} />
                </div>
              </div>

              {/* Cooldown status */}
              {cooldownActive && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                    <FaShieldAlt className="text-orange-600 text-xs" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-orange-800">Cooldown Active</p>
                    <p className="text-[11px] text-orange-600">{formatTime(cooldownRemaining)} remaining</p>
                  </div>
                </div>
              )}

              {/* Re-check button */}
              <button
                onClick={() => setShowPreTradeCheck(true)}
                className="text-[11px] text-blue-600 hover:underline"
              >
                Re-do check-in
              </button>
            </>
          )}
        </div>
      )}

      {view === 'settings' && (
        <SettingsPanel
          settings={settings}
          editSettings={editSettings}
          setEditSettings={setEditSettings}
          onSave={updateMindSettings}
        />
      )}
    </div>
  );
}

function Stat({ label, value, icon, color = '' }) {
  return (
    <div className="text-center">
      <div className="text-gray-400 text-[10px] mb-1 flex items-center justify-center gap-1">
        {icon} {label}
      </div>
      <div className={`text-sm font-bold text-gray-900 ${color}`}>{value}</div>
    </div>
  );
}

function SettingsPanel({ settings, editSettings, setEditSettings, onSave }) {
  const [local, setLocal] = useState(null);

  const startEdit = () => {
    setLocal({ ...settings });
    setEditSettings(true);
  };

  const handleSave = () => {
    onSave(local);
    setEditSettings(false);
    setLocal(null);
  };

  if (!editSettings) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-gray-900">Mind Guard Settings</h4>
          <button
            onClick={startEdit}
            className="px-3 py-1.5 rounded-lg text-[10px] font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
          >
            Edit
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-[11px]">
          <SettingRow label="Max Risk %" value={`${settings?.maxRiskPercent || DEFAULT_MIND_SETTINGS.maxRiskPercent}%`} />
          <SettingRow label="Max Trades/Day" value={settings?.maxTradesPerDay || DEFAULT_MIND_SETTINGS.maxTradesPerDay} />
          <SettingRow label="Min Time Between" value={`${settings?.minTimeBetweenTrades || DEFAULT_MIND_SETTINGS.minTimeBetweenTrades}s`} />
          <SettingRow label="Daily Loss Limit" value={`₹${(settings?.dailyLossLimit || DEFAULT_MIND_SETTINGS.dailyLossLimit).toLocaleString()}`} />
          <SettingRow label="Cooldown" value={`${settings?.cooldownMinutes || DEFAULT_MIND_SETTINGS.cooldownMinutes} min`} />
          <SettingRow label="Loss Streak Threshold" value={settings?.consecutiveLossThreshold || DEFAULT_MIND_SETTINGS.consecutiveLossThreshold} />
          <SettingRow label="Energy Threshold" value={settings?.energyThreshold || DEFAULT_MIND_SETTINGS.energyThreshold} />
        </div>

        <div className="border-t border-gray-100 pt-3">
          <h5 className="text-[10px] font-semibold text-gray-700 mb-2">Feature Toggles</h5>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <ToggleDisplay label="Pre-trade Check" enabled={settings?.enablePreTradeCheck} />
            <ToggleDisplay label="Cooldown" enabled={settings?.enableCooldown} />
            <ToggleDisplay label="Risk Guard" enabled={settings?.enableRiskGuard} />
            <ToggleDisplay label="Overtrade Guard" enabled={settings?.enableOvertradingGuard} />
            <ToggleDisplay label="Revenge Guard" enabled={settings?.enableRevengeGuard} />
            <ToggleDisplay label="Energy Guard" enabled={settings?.enableEnergyGuard} />
            <ToggleDisplay label="Daily Loss Guard" enabled={settings?.enableDailyLossGuard} />
          </div>
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-900">Edit Settings</h4>
        <div className="flex gap-2">
          <button
            onClick={() => { setEditSettings(false); setLocal(null); }}
            className="px-3 py-1.5 rounded-lg text-[10px] font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 rounded-lg text-[10px] font-bold bg-blue-600 text-white hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <NumberInput label="Max Risk %" value={local?.maxRiskPercent} field="maxRiskPercent" onChange={setLocal} data={local} />
        <NumberInput label="Max Trades/Day" value={local?.maxTradesPerDay} field="maxTradesPerDay" onChange={setLocal} data={local} />
        <NumberInput label="Min Time (s)" value={local?.minTimeBetweenTrades} field="minTimeBetweenTrades" onChange={setLocal} data={local} />
        <NumberInput label="Daily Loss Limit (₹)" value={local?.dailyLossLimit} field="dailyLossLimit" onChange={setLocal} data={local} />
        <NumberInput label="Cooldown (min)" value={local?.cooldownMinutes} field="cooldownMinutes" onChange={setLocal} data={local} />
        <NumberInput label="Loss Streak Threshold" value={local?.consecutiveLossThreshold} field="consecutiveLossThreshold" onChange={setLocal} data={local} />
        <NumberInput label="Energy Threshold" value={local?.energyThreshold} field="energyThreshold" onChange={setLocal} data={local} />
      </div>

      <div className="border-t border-gray-100 pt-3">
        <h5 className="text-[10px] font-semibold text-gray-700 mb-2">Feature Toggles</h5>
        <div className="grid grid-cols-2 gap-2">
          {['enablePreTradeCheck', 'enableCooldown', 'enableRiskGuard', 'enableOvertradingGuard', 'enableRevengeGuard', 'enableEnergyGuard', 'enableDailyLossGuard'].map(key => (
            <ToggleInput key={key} label={key.replace('enable', '').replace(/([A-Z])/g, ' $1').trim()} field={key} data={local} onChange={setLocal} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SettingRow({ label, value }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-gray-900">{value}</span>
    </div>
  );
}

function ToggleDisplay({ label, enabled }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-1.5 h-1.5 rounded-full ${enabled !== false ? 'bg-green-500' : 'bg-gray-400'}`} />
      <span className="text-gray-600">{label}</span>
    </div>
  );
}

function NumberInput({ label, value, field, onChange, data }) {
  return (
    <div>
      <label className="text-[10px] font-medium text-gray-600 block mb-1">{label}</label>
      <input
        type="number"
        value={value || ''}
        onChange={e => onChange({ ...data, [field]: parseFloat(e.target.value) || 0 })}
        className="w-full bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}

function ToggleInput({ label, field, data, onChange }) {
  return (
    <label className="flex items-center gap-2 text-[10px] text-gray-600 cursor-pointer">
      <input
        type="checkbox"
        checked={data?.[field] !== false}
        onChange={e => onChange({ ...data, [field]: e.target.checked })}
        className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      {label}
    </label>
  );
}
